import { type ColumnHelper, createColumnHelper } from '@tanstack/react-table';
import { COMPARE_BY_KEYS } from '#src/state-machine/constants';
import { type getDatasetTableData } from '#src/state-machine/selectors';
import { type MachineContextType } from '#src/state-machine/types';
import {
  type OneDimensionData,
  type TwoDimensionData,
  type DatasetLabels,
  type TableType,
  DefaultFilterValues,
} from '#src/types';
import {
  formatNumber,
  formatNumberWithDecimals,
} from '#src/utils/format-number';
import { translate } from '#src/utils/i18n/translate';
import { getMonth } from '#src/utils/months-labels';

export function buildColumns(
  tableData: ReturnType<typeof getDatasetTableData>,
  shouldShowMonths: boolean,
  context: MachineContextType,
  datasetLabels: DatasetLabels | null,
  tableType: TableType | null,
  totals:
    | { total: number }
    | { columnTotals: Record<string, number>; columns: string[] }
    | null
) {
  const {
    compareBy,
    level,
    category,
    fundingSource,
    levelCompare,
    categoryCompare,
    fundingSourceCompare,
    tableDataView: originalTableDataView,
    viewType,
    data: { fundingSources },
  } = context;

  // For chart/map views, always use absolute values in CSV export
  const tableDataView =
    viewType === 'chart' || viewType === 'map'
      ? 'absolute'
      : originalTableDataView;

  const hasFundingSources = fundingSources != null;
  const getDimensionHeader = (dimension: string, value: string) => {
    if (dimension === 'fundingSource') {
      return translate('funding_source.label');
    }

    if (
      compareBy.includes(dimension as 'level' | 'category' | 'fundingSource')
    ) {
      return datasetLabels?.[dimension as 'level' | 'category']?.[
        value
      ] as string;
    }
    return translate(`table-view.columns.${dimension}`);
  };

  const getDateColumns = (
    helper: ColumnHelper<any>,
    getDate: (d: any) => number
  ) => {
    const dateColumns = [
      helper.accessor(
        (d) => {
          const value = getDate(d);
          if (value == null) return '-';
          const date = new Date(value * 1000);
          return date.getUTCFullYear().toString();
        },
        {
          id: 'year',
          header: translate('table-view.columns.year'),
          footer: translate('table-view.footer.total'),
          size: 100,
          minSize: 80,
        }
      ),
    ];

    if (shouldShowMonths) {
      dateColumns.push(
        helper.accessor(
          (d) => {
            const date = new Date(getDate(d) * 1000);
            return getMonth(date.getUTCMonth().toString());
          },
          {
            id: 'month',
            header: translate('table-view.columns.month'),
            size: 120,
            minSize: 100,
          }
        )
      );
    }
    return dateColumns;
  };

  const getValueCell = (d: OneDimensionData, total: number) => {
    if (tableDataView === 'absolute') {
      return formatNumber(d.value);
    }
    if (tableDataView === 'share') {
      const share = (d.value / total) * 100;
      return `${formatNumberWithDecimals(share)}%`;
    }
    const sign = d.percentGrowthLastYear > 0 ? '+' : '';
    return `${sign}${formatNumberWithDecimals(d.percentGrowthLastYear, 2)}%`;
  };

  const getValueFooterCell = (total: number) => () => {
    if (tableDataView === 'share') return '100%';
    if (tableDataView === 'percentage') {
      if (context.selectedYears.length > 1) {
        return '-';
      }
      const sign = total > 0 ? '+' : '';
      return `${sign}${formatNumberWithDecimals(total, 2)}%`;
    }
    return formatNumber(total);
  };

  switch (tableType) {
    case 'oneDimension': {
      const columnHelper = createColumnHelper<OneDimensionData>();
      const total = totals && 'total' in totals ? totals.total : 0;
      return [
        ...getDateColumns(columnHelper, (d) => d.date),
        columnHelper.accessor((d) => datasetLabels?.level[d.level] ?? d.level, {
          id: 'level',
          header:
            levelCompare.length === 0
              ? translate('table-view.columns.level')
              : (datasetLabels?.level[level] as string),
          size: 200,
          minSize: 150,
        }),
        columnHelper.accessor(
          (d) => datasetLabels?.category[d.category] ?? d.category,
          {
            id: 'category',
            header:
              categoryCompare.length === 0
                ? translate('table-view.columns.category')
                : (datasetLabels?.category[category] as string),
            size: 200,
            minSize: 150,
          }
        ),
        ...(hasFundingSources
          ? [
              columnHelper.accessor(
                (d) =>
                  datasetLabels?.fundingSource[d.fundingSource] ??
                  d.fundingSource,
                {
                  id: 'fundingSource',
                  header:
                    fundingSourceCompare.length > 0
                      ? translate('table-view.columns.fundingSource')
                      : (datasetLabels?.fundingSource[fundingSource] as string),
                  size: 200,
                  minSize: 150,
                }
              ),
            ]
          : []),
        columnHelper.accessor((d) => getValueCell(d, total), {
          id: `value-${tableDataView}`,
          header: datasetLabels?.datasetName,
          footer: getValueFooterCell(total),
          size: 180,
          minSize: 120,
          meta: { numeric: true },
        }),
      ];
    }
    case 'twoDimensions': {
      const columnHelper = createColumnHelper<OneDimensionData>();
      const total = totals && 'total' in totals ? totals.total : 0;
      return [
        ...getDateColumns(columnHelper, (d) => d.date),
        columnHelper.accessor((d) => datasetLabels?.level[d.level] ?? d.level, {
          id: 'level',
          header:
            level === DefaultFilterValues.level
              ? translate(`table-view.columns.level`)
              : (datasetLabels?.level[level] as string),
          size: 200,
          minSize: 150,
        }),
        columnHelper.accessor(
          (d) => datasetLabels?.category[d.category] ?? d.category,
          {
            id: 'category',
            header:
              category === DefaultFilterValues.category
                ? translate(`table-view.columns.category`)
                : (datasetLabels?.category[category] as string),
            size: 200,
            minSize: 150,
          }
        ),
        ...(hasFundingSources
          ? [
              columnHelper.accessor(
                (d) =>
                  datasetLabels?.fundingSource[d.fundingSource] ??
                  d.fundingSource,
                {
                  id: 'fundingSource',
                  header: translate('table-view.columns.fundingSource'),
                  size: 200,
                  minSize: 150,
                }
              ),
            ]
          : []),
        columnHelper.accessor((d) => getValueCell(d, total), {
          id: `value-${tableDataView}`,
          header: datasetLabels?.datasetName,
          footer: getValueFooterCell(total),
          size: 180,
          minSize: 120,
          meta: { numeric: true },
        }),
      ];
    }
    case 'threeDimensions': {
      const columnHelper = createColumnHelper<TwoDimensionData>();
      // Use pre-computed columns from totals to avoid re-iterating
      const cols =
        totals && 'columns' in totals
          ? totals.columns
          : Object.keys(tableData[0] ?? {}).sort();
      const otherDimension = compareBy[0];
      const missingDimension = Object.keys(COMPARE_BY_KEYS).find(
        (key) =>
          !compareBy.includes(key as 'level' | 'category' | 'fundingSource')
      ) as 'level' | 'category' | 'fundingSource';
      const compareByValues = {
        level: level,
        category: category,
        fundingSource: 'fundingSource',
      };
      const otherDimensionValue = compareByValues[otherDimension];
      const missingDimensionValue = compareByValues[missingDimension];

      const columnTotals =
        totals && 'columnTotals' in totals ? totals.columnTotals : {};

      return [
        ...getDateColumns(columnHelper, (d) => {
          const key = Object.keys(d)[0];
          return d[key]?.date;
        }),
        ...(hasFundingSources
          ? [
              columnHelper.accessor(
                (d) => {
                  const key = Object.keys(d)[0];
                  const value = d[key]?.[missingDimension];
                  if (value == null) return '-';
                  return datasetLabels?.[missingDimension][value] ?? value;
                },
                {
                  id: missingDimension,
                  header: getDimensionHeader(
                    missingDimension,
                    missingDimensionValue
                  ),
                  size: 200,
                  minSize: 150,
                }
              ),
            ]
          : []),
        columnHelper.accessor(
          (d) => {
            const key = Object.keys(d)[0];
            const value = d[key]?.[otherDimension];
            if (value == null) return '-';
            return datasetLabels?.[otherDimension][value] ?? value;
          },
          {
            id: otherDimension,
            header: getDimensionHeader(otherDimension, otherDimensionValue),
            size: 200,
            minSize: 150,
          }
        ),
        ...cols.map((column) => {
          const columnTotal = columnTotals[column];
          return columnHelper.accessor(
            (row) => {
              const cell = row[column];
              if (cell == null) return '-';
              return getValueCell(cell, columnTotal);
            },
            {
              id: `value-${column}-${tableDataView}`,
              sortingFn: (rowA, rowB) => {
                const cellA = rowA.original[column];
                const cellB = rowB.original[column];
                return (cellA?.value ?? 0) - (cellB?.value ?? 0);
              },
              header: datasetLabels?.[compareBy[1]][column] as string,
              size: 180,
              minSize: 120,
              footer: getValueFooterCell(columnTotal ?? 0),
              meta: { numeric: true },
            }
          );
        }),
      ];
    }
    default: {
      return [];
    }
  }
}
